import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import {
  AdminModal,
  AdminInput,
  AdminSelect,
  AdminTextarea,
} from '../../components/admin/AdminUI';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { Plus, Pencil, Trash2, Power } from 'lucide-react';

const AMP_OPTIONS = ['30', '50', '30/50'];

const emptyForm = () => ({
  number: '',
  name: '',
  description: '',
  image_url: '',
  amp_service: '30/50',
  big_rig_ok: true,
  status: 'available',
  grid_x: '1',
  grid_y: '1',
  notes: '',
});

export const AdminRvSites = () => {
  const { authedAxios } = useAdminAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/rv-sites');
      setSites(data.sites || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load RV sites');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    const nextNum = sites.length ? Math.max(...sites.map((s) => s.number)) + 1 : 1;
    setForm({ ...emptyForm(), number: String(nextNum) });
    setModal({ mode: 'add' });
  };

  const openEdit = (s) => {
    setForm({
      number: String(s.number ?? ''),
      name: s.name || '',
      description: s.description || '',
      image_url: s.image_url || '',
      amp_service: s.amp_service || '30/50',
      big_rig_ok: !!s.big_rig_ok,
      status: s.status || 'available',
      grid_x: String(s.grid_x ?? 1),
      grid_y: String(s.grid_y ?? 1),
      notes: s.notes || '',
    });
    setModal({ mode: 'edit', site: s });
  };

  const close = () => {
    setModal(null);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        number: parseInt(form.number) || 0,
        grid_x: parseInt(form.grid_x) || 1,
        grid_y: parseInt(form.grid_y) || 1,
      };
      if (modal.mode === 'add') {
        await authedAxios().post('/api/admin/rv-sites', payload);
      } else {
        await authedAxios().patch(`/api/admin/rv-sites/${modal.site.id}`, payload);
      }
      close();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (s) => {
    const next = s.status === 'available' ? 'out_of_service' : 'available';
    try {
      await authedAxios().patch(`/api/admin/rv-sites/${s.id}`, { status: next });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete site #${s.number} (${s.name})? This cannot be undone.`)) return;
    try {
      await authedAxios().delete(`/api/admin/rv-sites/${s.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const onField = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  if (loading) return <p className="text-[#F4F1EA]/60">Loading RV sites...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-rv-sites-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-[#F4F1EA]">RV Sites</h2>
          <p className="text-[#F4F1EA]/60 mt-1">
            {sites.length} sites · Drag positions are set via the Map Position fields (grid X/Y).
          </p>
        </div>
        <Button
          data-testid="admin-add-rv-site-btn"
          onClick={openAdd}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Site
        </Button>
      </div>

      <div className="overflow-x-auto bg-[#0B132B] border border-[#B8860B]/30 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Amp</th>
              <th className="text-left p-3">Big Rig</th>
              <th className="text-left p-3">Map Position</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-[#F4F1EA]/60">
                  No RV sites yet.
                </td>
              </tr>
            )}
            {sites.map((s) => (
              <tr
                key={s.id}
                data-testid={`admin-rv-site-row-${s.id}`}
                className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
              >
                <td className="p-3 text-[#B8860B] font-bold">#{s.number}</td>
                <td className="p-3 text-[#F4F1EA]">
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-[#F4F1EA]/60 max-w-md line-clamp-1">{s.description}</p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">{s.amp_service}</td>
                <td className="p-3 text-[#F4F1EA]/85">{s.big_rig_ok ? 'Yes' : 'No'}</td>
                <td className="p-3 text-[#F4F1EA]/70 text-xs">
                  row {s.grid_y}, col {s.grid_x}
                </td>
                <td className="p-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      s.status === 'available'
                        ? 'bg-[#B8860B] text-[#F4F1EA]'
                        : 'bg-red-900/40 text-red-300 border border-red-500/40'
                    }`}
                  >
                    {s.status === 'available' ? 'Available' : 'Out of Service'}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-toggle-rv-site-${s.id}`}
                    onClick={() => toggleStatus(s)}
                    className="inline-flex items-center px-2 py-1 text-[#B8860B] hover:bg-[#B8860B]/15 rounded mr-1"
                    title="Toggle status"
                  >
                    <Power size={16} />
                  </button>
                  <button
                    data-testid={`admin-edit-rv-site-${s.id}`}
                    onClick={() => openEdit(s)}
                    className="inline-flex items-center px-2 py-1 text-[#B8860B] hover:bg-[#B8860B]/15 rounded mr-1"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`admin-delete-rv-site-${s.id}`}
                    onClick={() => remove(s)}
                    className="inline-flex items-center px-2 py-1 text-red-400 hover:bg-red-500/15 rounded"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        testid="admin-rv-site-modal"
        open={!!modal}
        onClose={close}
        title={modal?.mode === 'add' ? 'Add RV Site' : 'Edit RV Site'}
        footer={
          <>
            <Button variant="outline" onClick={close} className="border-[#B8860B]/40 text-[#F4F1EA]">
              Cancel
            </Button>
            <Button
              data-testid="admin-rv-site-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <AdminInput label="Site Number" type="number" value={form.number} onChange={onField('number')} required />
          <AdminInput label="Site Name" value={form.name} onChange={onField('name')} required />
          <AdminSelect label="Amp Service" value={form.amp_service} onChange={onField('amp_service')}>
            {AMP_OPTIONS.map((a) => (
              <option key={a} value={a}>{a} amp</option>
            ))}
          </AdminSelect>
          <AdminSelect label="Status" value={form.status} onChange={onField('status')}>
            <option value="available">Available</option>
            <option value="out_of_service">Out of Service</option>
          </AdminSelect>
          <AdminInput label="Map Row (1–3)" type="number" min="1" max="5" value={form.grid_y} onChange={onField('grid_y')} />
          <AdminInput label="Map Column (1–6)" type="number" min="1" max="8" value={form.grid_x} onChange={onField('grid_x')} />
          <div className="col-span-2">
            <ImageUploader
              testid="rv-site-image-uploader"
              label="Photo"
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
            />
          </div>
        </div>
        <AdminTextarea label="Description (shown to guests)" value={form.description} onChange={onField('description')} />
        <AdminTextarea label="Internal Notes" value={form.notes} onChange={onField('notes')} />
        <label className="flex items-center gap-2 cursor-pointer text-[#F4F1EA]">
          <input
            type="checkbox"
            checked={form.big_rig_ok}
            onChange={onField('big_rig_ok')}
            className="accent-[#B8860B]"
          />
          <span>Big rig friendly</span>
        </label>
      </AdminModal>
    </div>
  );
};

export default AdminRvSites;
